import { useCallback, useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { isFirefox, isIos, isSafari, useGroupHover } from '@alltrails/core';
import { POISearchResult } from '@alltrails/search/types/algoliaResultTypes';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { FONT_SIZE_100, FONT_SIZE_200 } from '@alltrails/denali/tokens';
import useMap from '../../../hooks/useMap';
import { useAppDispatch } from '../../../redux';
import { updateCursor } from '../../../redux/reducer';
import { imageUrlToBase64 } from '../../../utils/imageUrlToBase64';
import { coordinatesToSerializedCoordinates } from '../../../utils/serializedCoordinates';
import { defaultPOIPinLayerLayoutProps, defaultPOIPinLayerPaintProps, getLayerBeforeId, getLargePOIPinsId, poiLargeId } from '../../../utils/layers';
import { poiPinHeightLg, poiPinImageWidthLg, poiPinVerticalOffset } from '../../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages, { preloadSvg, SvgDefinition } from '../../../utils/useSvgImages';
import createPOIIconPinSvg, { imageNamePrefix as iconPrefix } from '../../../utils/svgDefinitions/createPOIIconPinSvg';
import createPOIPhotoPinSvg, { getPOIPhotoName, imageNamePrefix as photoPrefix } from '../../../utils/svgDefinitions/createPOIPhotoPinSvg';
import { useDelayedOnStyleChange } from '../../../hooks/useDelayedOnStyleChange';
import type POIResultProperties from '../POIResults/POIResultProperties';

const poiFocusedSourceId = 'poi-focused-source';

type POIPinLargeProps = { id: string; onClick?: (ev: any) => void; poi?: POISearchResult };

const POIPinLarge = ({ id, onClick, poi }: POIPinLargeProps) => {
  const dispatch = useAppDispatch();
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();

  const poiFeatures = useMemo(() => {
    if (!poi?._geoloc) {
      return [];
    }

    return [
      {
        geometry: { type: 'Point', coordinates: [poi._geoloc.lng, poi._geoloc.lat] },
        properties: {
          hasPhotos: !!poi.photos_count,
          id: poi.ID,
          name: poi.name,
          popularity: poi.popularity ?? 0,
          serializedCoordinates: coordinatesToSerializedCoordinates({ lat: poi._geoloc.lat, lng: poi._geoloc.lng }),
          type: poi.subtype
        },
        type: 'Feature'
      }
    ] as Feature<Point, POIResultProperties>[];
  }, [poi]);

  const defaultSvgDefinitions = useMemo(() => {
    if (!poi) return [];

    return [createPOIIconPinSvg(poi.subtype, 'md'), createPOIIconPinSvg(poi.subtype, 'lg')];
  }, [poi]);
  const { imagesAreLoaded, loadImage } = useSvgImages(defaultSvgDefinitions);

  const onClickInternal = useCallback(
    (ev: Event) => {
      onClick?.(ev);
      ev.preventDefault();
    },
    [onClick]
  );

  const onHover = useCallback(() => {
    dispatch(updateCursor('pointer'));
  }, [dispatch]);

  const onHoverEnd = useCallback(() => {
    dispatch(updateCursor(''));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, false, 0);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.on('click', getLargePOIPinsId(id), onClickInternal);
    !isMobile && onMouseEnter && map.on('mousemove', getLargePOIPinsId(id), onMouseEnter);
    !isMobile && onMouseLeave && map.on('mouseleave', getLargePOIPinsId(id), onMouseLeave);
    return () => {
      map.off('click', getLargePOIPinsId(id), onClickInternal);
      !isMobile && onMouseEnter && map.off('mousemove', getLargePOIPinsId(id), onMouseEnter);
      !isMobile && onMouseLeave && map.off('mouseleave', getLargePOIPinsId(id), onMouseLeave);
    };
  }, [id, isMobile, map, onClickInternal, onMouseEnter, onMouseLeave]);

  const loadPOIPin = useCallback(async () => {
    if (!poi) return;
    let imageData;
    try {
      imageData = await imageUrlToBase64(
        `/api/alltrails/points_of_interest/${poi.ID}/profile_photo?size=${poiPinImageWidthLg * 2}w${poiPinHeightLg * 2}h`
      );
    } catch {
      // Use the POI photo image name with the POI icon svg since these are fallbacks for when the image fails to load
      [
        { ...createPOIIconPinSvg(poi.subtype, 'md'), name: getPOIPhotoName(poi.ID, 'md') },
        { ...createPOIIconPinSvg(poi.subtype, 'lg'), name: getPOIPhotoName(poi.ID, 'lg') }
      ].forEach(definition => {
        loadImage(definition);
      });
      return;
    }

    if (!imageData) return;

    const definitions: SvgDefinition[] = [createPOIPhotoPinSvg(poi.ID, imageData, 'md'), createPOIPhotoPinSvg(poi.ID, imageData, 'lg')];
    definitions.forEach(async definition => {
      if (isSafari() || (isFirefox() && isIos())) {
        // Safari seems to have their own way of handling SVGs, and as a result the `onLoad` event for the image seems to fire prior to the full pin image being loaded.
        await preloadSvg(definition.svgString).then(() => {
          setTimeout(() => {
            loadImage(definition);
            map?.triggerRepaint();
          }, 100);
        });
      } else {
        loadImage(definition);
        map?.triggerRepaint();
      }
    });
  }, [loadImage, map, poi]);
  useDelayedOnStyleChange(loadPOIPin /* onStyleChange */, loadPOIPin /* onInitialLoad */);

  if (!map || !imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={`${poiFocusedSourceId}-${id}`} type="geojson" data={{ type: 'FeatureCollection', features: poiFeatures }}>
        <Layer
          source={`${poiFocusedSourceId}-${id}`}
          id={getLargePOIPinsId(id)}
          beforeId={getLayerBeforeId(map, poiLargeId)}
          type="symbol"
          layout={{
            ...defaultPOIPinLayerLayoutProps,
            'icon-image': [
              'case',
              ['==', ['get', 'hasPhotos'], true],
              ['concat', `${photoPrefix}-`, ['get', 'id'], ['case', isMobile, '-md', '-lg']],
              ['concat', `${iconPrefix}-`, ['get', 'type'], ['case', isMobile, '-md', '-lg']]
            ],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'text-anchor': 'top',
            'text-field': '{name}',
            'text-offset': [0, poiPinVerticalOffset / (isMobile ? FONT_SIZE_100 : FONT_SIZE_200)],
            'text-size': ['case', isMobile, FONT_SIZE_100, FONT_SIZE_200]
          }}
          paint={defaultPOIPinLayerPaintProps}
        />
      </Source>
    </>
  );
};

export default POIPinLarge;
